import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronLeft, ChevronRight,
  Camera, Video, Heart, Smile, Aperture, Film, Plane, Wand2, Scissors,
  BookOpen, Radio, Lamp, Lightbulb, Move, Sparkles, Clapperboard, Users,
  Star, ShieldCheck, MapPin, LayoutGrid, PartyPopper, Briefcase, Bird,
  Mountain, UtensilsCrossed, Baby, Home as HomeIcon, Package, User,
} from "@/lib/icons";
import { resolvePhotoUrl } from "@/lib/api";

// Lookup table of Lucide icons supported in category tiles.
const ICON_MAP = {
  Camera, Video, Heart, Smile, Aperture, Film, Plane, Wand2, Scissors,
  BookOpen, Radio, Lamp, Lightbulb, Move, Sparkles, Clapperboard, Users,
  Star, ShieldCheck, MapPin, LayoutGrid, PartyPopper, Briefcase, Bird,
  Mountain, UtensilsCrossed, Baby, Home: HomeIcon, Package, User,
};

/**
 * Horizontal, scrollable category strip. Shows ~5 tiles at a time on desktop
 * with a right arrow to reveal the rest. Fully touch-swipeable on mobile.
 *
 * Props:
 *   items:  [{ name, icon, icon_url, enabled }]
 *   linkPrefix: (optional) path prefix — clicking a tile navigates to
 *               `${linkPrefix}?category=<name>` (defaults to `/gomore`).
 */
export default function CategoryStrip({ items = [], linkPrefix = "/gomore" }) {
  const scrollerRef = useRef(null);
  const directionRef = useRef(1);
  const pausedRef = useRef(false);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const cats = (items || []).filter((c) => c && c.enabled !== false && c.name);

  const updateEdges = () => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    updateEdges();
    const el = scrollerRef.current;
    if (!el) return;
    const onScroll = () => updateEdges();
    el.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", updateEdges);
    return () => {
      el.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", updateEdges);
    };
  }, [cats.length]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    let frame;
    let previous = performance.now();
    let edgeTick = 0;
    const drift = (now) => {
      const elapsed = Math.min(now - previous, 40);
      previous = now;
      if (!pausedRef.current && el.scrollWidth > el.clientWidth) {
        const end = el.scrollWidth - el.clientWidth;
        if (el.scrollLeft >= end - 1) directionRef.current = -1;
        if (el.scrollLeft <= 1) directionRef.current = 1;
        el.scrollLeft += directionRef.current * elapsed * 0.018;
        if (++edgeTick % 12 === 0) updateEdges();
      }
      frame = window.requestAnimationFrame(drift);
    };
    frame = window.requestAnimationFrame(drift);
    return () => window.cancelAnimationFrame(frame);
  }, [cats.length]);

  const scrollBy = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    directionRef.current = dir;
    el.scrollBy({ left: dir * Math.min(el.clientWidth * 0.72, 520), behavior: "smooth" });
  };

  if (cats.length === 0) return null;

  return (
    <section className="relative container-wide" data-testid="home-category-strip">
      <div className="mb-4 flex items-end justify-between gap-3">
        <div>
          <div className="overline text-fuchsia-300">Categories</div>
          <h2 className="mt-1 font-display text-2xl md:text-3xl text-white">
            Explore by <span className="italic text-gradient">specialty</span>
          </h2>
        </div>
        <span className="hidden text-[10px] font-medium uppercase tracking-[0.2em] text-white/35 sm:block">
          Browse specialties
        </span>
      </div>

      <div
        className="category-orbit relative overflow-hidden rounded-[26px] md:rounded-[32px] border border-fuchsia-400/15 bg-[#100819]/90 px-2 py-4 md:px-3 md:py-5 shadow-[0_22px_70px_-40px_rgba(127,0,255,0.8)]"
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}
        onTouchStart={() => { pausedRef.current = true; }}
        onTouchEnd={() => { pausedRef.current = false; }}
        onFocusCapture={() => { pausedRef.current = true; }}
        onBlurCapture={() => { pausedRef.current = false; }}
      >
        <div aria-hidden className="category-accent-line pointer-events-none absolute left-[8%] top-0 h-px w-[42%] bg-[#9D00FF]/55" />
        <div
          ref={scrollerRef}
          className="cat-strip flex gap-2 overflow-x-auto px-9 py-2 md:gap-4 md:px-14"
          data-testid="cat-strip-scroller"
        >
          {cats.map((c, i) => (
            <CategoryTile key={`${c.name}-${i}`} cat={c} index={i}
              to={`${linkPrefix}?category=${encodeURIComponent(c.name)}`} />
          ))}
        </div>
        <div aria-hidden className="category-edge-fade pointer-events-none absolute inset-y-0 left-0 z-[5] w-12 bg-gradient-to-r from-[#100819] via-[#100819]/90 to-transparent md:w-16" />
        <div aria-hidden className="category-edge-fade pointer-events-none absolute inset-y-0 right-0 z-[5] w-12 bg-gradient-to-l from-[#100819] via-[#100819]/90 to-transparent md:w-16" />
        <div className="absolute left-2 top-1/2 -translate-y-1/2 z-10">
          <ArrowBtn dir={-1} disabled={!canPrev} onClick={() => scrollBy(-1)} testid="cat-scroll-prev-panel" />
        </div>
        <div className="absolute right-2 top-1/2 -translate-y-1/2 z-10">
          <ArrowBtn dir={1} disabled={!canNext} onClick={() => scrollBy(1)} testid="cat-scroll-next-panel" />
        </div>
      </div>
    </section>
  );
}

function CategoryTile({ cat, to, index }) {
  const Icon = ICON_MAP[cat.icon] || Camera;
  const uploaded = resolvePhotoUrl(cat.icon_url);
  const tones = [
    "bg-[#7F00FF]/18 border-[#9D00FF]/30 text-[#D7A5FF]",
    "bg-[#9D00FF]/14 border-[#C05CFF]/25 text-[#E1BDFF]",
    "bg-[#5D16A8]/22 border-[#8E46D4]/28 text-[#CFA1FF]",
  ];
  return (
    <Link
      to={to}
      data-testid={`cat-tile-${(cat.name || "").toLowerCase().replace(/\s+/g, "-")}`}
      className="cat-tile group snap-start shrink-0 flex w-[84px] md:w-[112px] flex-col items-center gap-2.5 rounded-2xl px-1 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#9D00FF]"
    >
      <span className="category-float relative" style={{ animationDelay: `${-(index % 7) * 0.65}s` }}>
        <span className={`category-tile-icon relative inline-flex h-[72px] w-[72px] md:h-[88px] md:w-[88px] items-center justify-center rounded-[20px] border backdrop-blur-sm ${tones[index % tones.length]} shadow-[0_16px_38px_-24px_rgba(157,0,255,0.95)] transition-all duration-500 group-hover:-translate-y-1 group-hover:border-[#9D00FF]/65 group-hover:bg-[#9D00FF]/24 group-hover:shadow-[0_20px_42px_-20px_rgba(157,0,255,0.9)]`}>
          {uploaded ? (
            <img src={uploaded} alt="" className="h-11 w-11 md:h-12 md:w-12 object-contain drop-shadow-[0_6px_12px_rgba(0,0,0,0.35)]" />
          ) : (
            <Icon size={34} strokeWidth={1.8} />
          )}
        </span>
      </span>
      <span className="category-tile-label max-w-[108px] line-clamp-2 text-center text-[11px] font-medium leading-tight text-white/70 transition-colors group-hover:text-white md:text-xs">
        {cat.name}
      </span>
    </Link>
  );
}

function ArrowBtn({ dir, disabled, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      aria-label={dir < 0 ? "Previous categories" : "More categories"}
      className="category-arrow flex h-9 w-9 items-center justify-center rounded-xl border border-[#9D00FF]/20 bg-[#12091D]/90 text-white shadow-lg backdrop-blur-md transition-all hover:border-[#9D00FF]/55 hover:bg-[#7F00FF]/25 disabled:pointer-events-none disabled:opacity-25"
    >
      {dir < 0 ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
    </button>
  );
}
